import { useState, useCallback, useMemo } from 'react';
import { Stepper } from 'Components/organisms';
import { Typography } from 'Components/atoms';
import { CustomActionDto } from '@ampsec/platform-client';
import { ACTION_LABELS, CUSTOM_ACTION_HEADERS, SUPPORTING_TEXT } from 'Core-utils/constants/constants';
import {
    AmplifierTrainingTemplateDefinition,
    AmplifierTrainingTemplateKey,
    getAmplifierTrainingTemplateByKey,
} from './amplifierTrainingTemplates';
import { AmplifierTrainingGeneralStep } from './AmplifierTrainingGeneralStep';
import { AmplifierTrainingConfigStep } from './AmplifierTrainingConfigStep';
import { useComplianceTrainingModules } from './useComplianceTrainingModules';

interface AmplifierTrainingStepperFormProps {
    templateKey: AmplifierTrainingTemplateKey;
    templates: AmplifierTrainingTemplateDefinition[];
    data: CustomActionDto;
    selectedSubjects: string[];
    onSelectedSubjectsChange: (subjects: string[]) => void;
    handleFieldChange: (id: string, value: string) => void;
    handleSave: () => void;
}

const AmplifierTrainingStepperForm = ({
    templateKey,
    templates,
    data,
    selectedSubjects,
    onSelectedSubjectsChange,
    handleFieldChange,
    handleSave,
}: AmplifierTrainingStepperFormProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState<string[]>([]);
    const { modules, loading: modulesLoading } = useComplianceTrainingModules();
    const template = getAmplifierTrainingTemplateByKey(templateKey, templates);
    const minModules = template?.minModules ?? 0;
    const lastStepIndex = 1;

    const validateStep = useCallback(
        (step: number): string[] => {
            const formErrors: string[] = [];
            if (step === 0 && !data.displayValue?.trim()) {
                formErrors.push(`${ACTION_LABELS[2]} ${SUPPORTING_TEXT}`);
            }
            if (step === 1 && templateKey !== 'scheduled') {
                if (selectedSubjects.length < minModules) {
                    formErrors.push('module');
                }
            }
            return formErrors;
        },
        [data.displayValue, minModules, selectedSubjects.length, templateKey],
    );

    const handleBackButtonClick = () => {
        setErrors([]);
        setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
    };

    const handleNextButtonClick = () => {
        const formErrors = validateStep(currentStep);
        if (formErrors.length > 0) {
            setErrors(formErrors);
            return;
        }
        setErrors([]);
        setCurrentStep((prevStep) => Math.min(prevStep + 1, lastStepIndex));
    };

    const isStepValid = useCallback((step: number) => validateStep(step).length === 0, [validateStep]);

    const isFormValid = useMemo(
        () => isStepValid(0) && isStepValid(1),
        [isStepValid],
    );

    const nextDisabled = currentStep === 0 && !isStepValid(0);
    const saveDisabled = !isFormValid;

    const handleSaveClick = () => {
        if (!isFormValid) {
            const formErrors = [...validateStep(0), ...validateStep(1)];
            setErrors(formErrors);
            if (!data.displayValue?.trim()) {
                setCurrentStep(0);
            } else if (formErrors.includes('module')) {
                setCurrentStep(1);
            }
            return;
        }
        setErrors([]);
        handleSave();
    };

    const renderHeading = (title: string) => (
        <Typography variant="body12" color="text-high-emphasis" style={{ textTransform: 'uppercase' }}>
            {title}
        </Typography>
    );

    const steps = [
        {
            stepperName: CUSTOM_ACTION_HEADERS[0],
            stepperContent: (
                <>
                    {renderHeading(CUSTOM_ACTION_HEADERS[0])}
                    <AmplifierTrainingGeneralStep
                        templateKey={templateKey}
                        templates={templates}
                        formData={data}
                        handleFieldChange={handleFieldChange}
                        errors={errors}
                    />
                </>
            ),
        },
        {
            stepperName: 'Training',
            stepperContent: (
                <div className="amplifier-training-step__content">
                    {renderHeading('Training configuration')}
                    <AmplifierTrainingConfigStep
                        templateKey={templateKey}
                        templates={templates}
                        modules={modules}
                        modulesLoading={modulesLoading}
                        selectedSubjects={selectedSubjects}
                        onSelectedSubjectsChange={onSelectedSubjectsChange}
                        errors={errors}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className="amplifier-training-stepper h-100">
            <Stepper
                steps={steps}
                currentStep={currentStep}
                handleBackButtonClick={handleBackButtonClick}
                handleNextButtonClick={handleNextButtonClick}
                handleSave={handleSaveClick}
                nextDisabled={nextDisabled}
                saveDisabled={saveDisabled}
            />
        </div>
    );
};

export default AmplifierTrainingStepperForm;
